// src/redux/slices/channelsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import API_ROUTES from '../../config/apiRoutes';

/**
 * POST /api/v2/channels/filter
 * Body: { byUserId, byChannelId, byKeyword, byShareUrl, byViewsCount, currentPage, limit }
 * Response: { channels, totalCount, currentPage, totalPages }
 */
export const fetchChannels = createAsyncThunk(
  'channels/fetchChannels',
  async (params = {}, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    const body = {
      currentPage: params.currentPage || 1,
      limit: params.limit || 10,
      byUserId: params.byUserId || '',
      byChannelId: params.byChannelId || '',
      byKeyword: params.byKeyword || '',
      byShareUrl: params.byShareUrl || '',
      ...(typeof params.byViewsCount === 'boolean' ? { byViewsCount: params.byViewsCount } : {}),
    };
    try {
      const res = await axios.post(API_ROUTES.CHANNELS.GET_ALL, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return {
        channels: res.data?.channels || [],
        total: res.data?.totalCount ?? 0,
        totalPages: res.data?.totalPages ?? 1,
        currentPage: res.data?.currentPage ?? body.currentPage,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch channels');
    }
  }
);

/**
 * POST /api/v2/channels/create — multipart/form-data.
 * Pass `formData` already constructed by the caller.
 */
export const createChannel = createAsyncThunk(
  'channels/create',
  async (formData, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      const res = await axios.post(API_ROUTES.CHANNELS.CREATE, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create channel');
    }
  }
);

/**
 * PATCH /api/v2/channels/update/:id — multipart/form-data.
 */
export const updateChannel = createAsyncThunk(
  'channels/update',
  async ({ id, formData }, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      const res = await axios.patch(API_ROUTES.CHANNELS.UPDATE(id), formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update channel');
    }
  }
);

/**
 * PATCH /api/v2/channels/admin/update-status/:channelId
 * Body: { status }
 */
export const updateChannelStatus = createAsyncThunk(
  'channels/updateStatus',
  async ({ channelId, status }, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      const res = await axios.patch(
        API_ROUTES.CHANNELS.UPDATE_STATUS(channelId),
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return { channelId, status, data: res.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update channel status');
    }
  }
);

const channelsSlice = createSlice({
  name: 'channels',
  initialState: {
    channels: [],
    total: 0,
    totalPages: 1,
    currentPage: 1,
    loading: false,
    saving: false,
    error: null,
  },
  reducers: {
    clearChannelsError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChannels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChannels.fulfilled, (state, action) => {
        state.loading = false;
        state.channels = action.payload.channels;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchChannels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createChannel.pending, (state) => { state.saving = true; })
      .addCase(createChannel.fulfilled, (state) => { state.saving = false; })
      .addCase(createChannel.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      .addCase(updateChannel.pending, (state) => { state.saving = true; })
      .addCase(updateChannel.fulfilled, (state) => { state.saving = false; })
      .addCase(updateChannel.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      .addCase(updateChannelStatus.fulfilled, (state, action) => {
        const { channelId, status } = action.payload;
        const idx = state.channels.findIndex((c) => c._id === channelId);
        if (idx >= 0) state.channels[idx].status = status;
      })
      .addCase(updateChannelStatus.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearChannelsError } = channelsSlice.actions;
export default channelsSlice.reducer;
